import React from 'react'
import { useHistory } from 'react-router'

import { MemberCard } from '../../components/MemberCard'

import {
  routes,
  IndexLayoutOld,
  IndexLayoutErrorOld,
  IndexLayoutLoadingOld,
} from '@sketch/modules-common'

import { Avatar, WorkspaceLogo } from '@sketch/components'
import {
  useGetPendingWorkspaceMembershipQuery,
  useAcceptWorkspaceInviteMutation,
} from '@sketch/gql-types'

import { ReactComponent as ArrowsRightLeftIcon } from '@sketch/icons/img-arrows-right-left-17'

import {
  ArrowIconWrapper,
  Header,
  RoleText,
  StyledButton,
  StyledSubtitle,
  StyledTitle,
  Wrapper,
  WrapperMemberCard,
} from '../AcceptWorkspaceInviteView/AcceptWorkspaceInviteView.styles'
import { LocationState as CreateWorkspaceDoneLocationState } from '../CreateWorkspaceDoneView'

interface WorkspaceInvitePendingViewProps {
  membershipId: string
}

export const WorkspaceInvitePendingView = ({
  membershipId,
}: WorkspaceInvitePendingViewProps) => {
  const history = useHistory()

  const { data, loading } = useGetPendingWorkspaceMembershipQuery({
    variables: { membershipId },
  })

  const [acceptInvite, { loading: loadingAcceptInvitation }] =
    useAcceptWorkspaceInviteMutation({
      variables: { membershipId },
      onError: 'show-toast',
      onCompleted: data => {
        const { identifier: acceptedWorkspaceId } =
          data.acceptWorkspaceInvite.membership.workspace
        const locationState: CreateWorkspaceDoneLocationState = {
          currentUserFlow: 'accept-workspace-invite',
        }
        history.replace(
          routes.WORKSPACE_CREATE_DONE.create({
            workspaceId: acceptedWorkspaceId,
          }),
          locationState
        )
      },
    })

  if (loading) {
    return <IndexLayoutLoadingOld />
  }

  if (!data?.pendingWorkspaceMembership) {
    return <IndexLayoutErrorOld />
  }

  const { pendingWorkspaceMembership } = data
  const { workspace, user, invite, isEditor } = pendingWorkspaceMembership

  const workspaceName = workspace.name
  const userName = user?.name ?? invite?.email ?? ''
  const userEmail = user?.email ?? invite?.email ?? ''
  const avatarSrc = user?.avatar?.large ?? user?.avatar?.small ?? undefined
  const roleText = isEditor ? 'Editor' : 'Viewer'

  return (
    <IndexLayoutOld headerLink="workspace-settings" maxContainerWidth="100vw">
      <StyledSubtitle>You&apos;ve been invited to join</StyledSubtitle>
      <StyledTitle>{workspaceName}</StyledTitle>

      <Header>
        <Avatar size="64px" src={avatarSrc} name={userName} />
        <ArrowIconWrapper>
          <ArrowsRightLeftIcon />
        </ArrowIconWrapper>
        <WorkspaceLogo
          workspaceName={workspaceName}
          size="64px"
          src={workspace.avatar?.large ?? undefined}
        />
      </Header>

      <Wrapper>
        <WrapperMemberCard>
          <MemberCard name={userName} email={userEmail} avatarSrc={avatarSrc} />
          <RoleText>
            Invited as <b>{roleText}</b>
          </RoleText>
        </WrapperMemberCard>

        <StyledButton
          variant="primary"
          loading={loadingAcceptInvitation}
          onClick={() => acceptInvite()}
          fill
        >
          Join {workspaceName}
        </StyledButton>
      </Wrapper>
    </IndexLayoutOld>
  )
}
